import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Play, Pause, Volume2, Maximize, Heart, Share2, MessageCircle, Users, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  MobileHeader, MobileBottomNav, DesktopHeader 
} from '@/components/layout/MobileLayout';
import { TipArtistModal } from '@/components/modals/TipArtistModal';
import { mockEvents } from '@/lib/mockData';

export function UserLivestreamView() {
  const { eventId } = useParams();
  const event = mockEvents.find(e => e.id === eventId) || mockEvents[0];
  const [showTipModal, setShowTipModal] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div className="min-h-screen bg-background" data-testid="user-livestream-view">
      {/* Mobile Header */}
      <MobileHeader />
      
      {/* Desktop Header */}
      <DesktopHeader />
      
      <main className="pt-14 lg:pt-16">
        <div className="flex flex-col lg:flex-row h-[calc(100vh-4rem)]">
          {/* Video Player */}
          <div className="flex-1 relative bg-black">
            {/* Video placeholder */}
            <div className="absolute inset-0 flex items-center justify-center">
              <img 
                src={event.coverImage || event.image} 
                alt={event.title}
                className="w-full h-full object-cover opacity-50"
              />
              {!isPlaying && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <button 
                      className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center mx-auto mb-4 cursor-pointer hover:scale-110 transition-transform"
                      onClick={() => setIsPlaying(true)}
                    >
                      <Play className="w-10 h-10 text-primary-foreground ml-1" fill="currentColor" />
                    </button>
                    <p className="text-white text-lg">Click to play livestream</p>
                  </div>
                </div>
              )}
            </div>

            {/* Live badge */}
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span className="px-3 py-1 bg-red-600 text-white text-sm font-semibold rounded-full flex items-center gap-1">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                LIVE
              </span>
              <span className="px-3 py-1 bg-black/50 text-white text-sm rounded-full flex items-center gap-1">
                <Users className="w-4 h-4" />
                2,547 watching
              </span>
            </div>

            {/* Video controls */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button 
                    className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
                    onClick={() => setIsPlaying(!isPlaying)}
                  >
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                  </button>
                  <button className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30">
                    <Volume2 className="w-5 h-5" />
                  </button>
                  <div className="flex-1 h-1 bg-white/30 rounded-full max-w-[200px]">
                    <div className="w-3/4 h-full bg-primary rounded-full"></div>
                  </div>
                </div>
                <button className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30">
                  <Maximize className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Chat Sidebar */}
          <div className="w-full lg:w-96 bg-card border-l border-border flex flex-col">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold text-white">Live Chat</h3>
              <p className="text-sm text-muted-foreground">{event.title}</p>
            </div>

            {/* Chat messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {[
                { user: 'JohnDoe', message: 'This is amazing! 🔥', time: '2m ago' },
                { user: 'MusicLover', message: 'Best concert ever!', time: '1m ago' },
                { user: 'ConcertFan', message: 'The sound quality is incredible', time: '45s ago' },
                { user: 'NightOwl', message: 'Who else is watching from NYC?', time: '30s ago' },
                { user: 'FestivalVibes', message: 'Can\'t wait for the next song 🎵', time: '15s ago' },
              ].map((chat, i) => (
                <div key={i} className="flex gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary/30 flex items-center justify-center text-xs text-primary">
                    {chat.user[0]}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-primary">{chat.user}</span>
                      <span className="text-xs text-muted-foreground">{chat.time}</span>
                    </div>
                    <p className="text-sm text-white">{chat.message}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Chat input */}
            <div className="p-4 border-t border-border">
              <div className="flex gap-2">
                <input 
                  type="text" 
                  placeholder="Send a message..." 
                  className="flex-1 bg-secondary border border-border rounded-lg px-4 py-2 text-white placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                />
                <Button className="bg-primary text-primary-foreground">
                  <MessageCircle className="w-4 h-4" />
                </Button>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 mt-3">
                <Button 
                  variant="outline" 
                  className="flex-1 border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                  onClick={() => setShowTipModal(true)}
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Tip Artist
                </Button>
                <Button variant="outline" className="border-border">
                  <Heart className="w-4 h-4" />
                </Button>
                <Button variant="outline" className="border-border">
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />

      {/* Tip Artist Modal */}
      <TipArtistModal 
        isOpen={showTipModal}
        onClose={() => setShowTipModal(false)}
        artistId={event.artists?.[0]?.id}
        eventId={eventId}
      />
    </div>
  );
}

export default UserLivestreamView;
